import { messageErreur } from '@imp/shared/message-erreur';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Plus, Trash2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import {
  type BuilderLigne,
  creerQuestionnaire,
  getQuestionnaireBuilder,
  majQuestionnaire,
} from '@/modules/qcm/builder';

const TVA_OPTIONS = [
  { code: '10', label: '10 % (rénovation)' },
  { code: '20', label: '20 % (standard)' },
  { code: '5.5', label: '5,5 % (énergie)' },
  { code: '0', label: '0 %' },
];

function ligneVide(): BuilderLigne {
  return { kind: 'quantite', question: '', unite: 'm²', prix: '', tvaCode: '10', libelle: '' };
}

export function QcmBuilderPage() {
  const { id } = useParams();
  const isEdit = Boolean(id);
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [nom, setNom] = useState('');
  const [description, setDescription] = useState('');
  const [couleur, setCouleur] = useState('#1D4595');
  const [lignes, setLignes] = useState<BuilderLigne[]>([ligneVide()]);

  const editQ = useQuery({
    queryKey: ['questionnaire-builder', id],
    queryFn: () => getQuestionnaireBuilder(id ?? ''),
    enabled: isEdit,
  });
  useEffect(() => {
    const d = editQ.data;
    if (!d) return;
    setNom(d.nom);
    setDescription(d.description);
    setCouleur(d.couleur);
    setLignes(d.lignes.length > 0 ? d.lignes : [ligneVide()]);
  }, [editQ.data]);

  const setLigne = (i: number, patch: Partial<BuilderLigne>) => {
    setLignes((ls) => ls.map((l, j) => (j === i ? { ...l, ...patch } : l)));
  };

  const saveMut = useMutation({
    mutationFn: async () => {
      const input = { nom, description, couleur, lignes };
      if (isEdit && id) await majQuestionnaire(id, input);
      else await creerQuestionnaire(input);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['questionnaires'] });
      navigate('/devis/qcm');
    },
  });

  const canSave = nom.trim() !== '' && lignes.some((l) => l.question.trim() !== '');

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <Button asChild variant="ghost" size="icon">
          <Link to="/devis/qcm" aria-label="Retour">
            <ArrowLeft className="h-4 w-4" />
          </Link>
        </Button>
        <div>
          <h1 className="text-2xl font-bold tracking-tight">
            {isEdit ? 'Modifier le devis guidé' : 'Nouveau devis guidé'}
          </h1>
          <p className="text-muted-foreground">
            Donnez un nom, puis listez les lignes : chacune devient une question qui alimente le
            devis.
          </p>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>La prestation</CardTitle>
        </CardHeader>
        <CardContent className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-1.5">
            <Label htmlFor="qg_nom">Nom *</Label>
            <Input
              id="qg_nom"
              value={nom}
              placeholder="Isolation des combles, Démoussage…"
              onChange={(e) => {
                setNom(e.target.value);
              }}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="qg_couleur">Couleur</Label>
            <input
              id="qg_couleur"
              type="color"
              value={couleur}
              onChange={(e) => {
                setCouleur(e.target.value);
              }}
              className="h-12 w-16 cursor-pointer rounded-md border border-input bg-card p-1 lg:h-11"
            />
          </div>
          <div className="space-y-1.5 sm:col-span-2">
            <Label htmlFor="qg_desc">Description</Label>
            <Textarea
              id="qg_desc"
              value={description}
              onChange={(e) => {
                setDescription(e.target.value);
              }}
            />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Les lignes du devis</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-muted-foreground">
            <strong>Quantité</strong> : on demande un nombre (ex : surface) → la ligne = nombre ×
            prix. <strong>Oui / Non</strong> : si « oui », la ligne est ajoutée (1 × prix).
          </p>

          {lignes.map((l, i) => (
            <div key={i} className="space-y-3 rounded-md border p-3">
              <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                <div className="space-y-1">
                  <Label>Type</Label>
                  <Select
                    value={l.kind}
                    onChange={(e) => {
                      setLigne(i, { kind: e.target.value as BuilderLigne['kind'] });
                    }}
                  >
                    <option value="quantite">Quantité (nombre)</option>
                    <option value="ouinon">Oui / Non</option>
                    <option value="texte">Texte libre (à écrire)</option>
                  </Select>
                </div>
                <div className="space-y-1 lg:col-span-3">
                  <Label>Question posée</Label>
                  <Input
                    value={l.question}
                    placeholder={
                      l.kind === 'quantite'
                        ? 'Ex : Surface à isoler (m²) ?'
                        : l.kind === 'ouinon'
                          ? 'Ex : Échafaudage nécessaire ?'
                          : 'Ex : Précisez les travaux supplémentaires'
                    }
                    onChange={(e) => {
                      setLigne(i, { question: e.target.value });
                    }}
                  />
                </div>
                {l.kind === 'quantite' ? (
                  <div className="space-y-1">
                    <Label>Unité</Label>
                    <Input
                      value={l.unite}
                      placeholder="m², ml, u…"
                      onChange={(e) => {
                        setLigne(i, { unite: e.target.value });
                      }}
                    />
                  </div>
                ) : null}
                <div className="space-y-1">
                  <Label>Prix unitaire HT (€)</Label>
                  <Input
                    inputMode="decimal"
                    value={l.prix}
                    placeholder="0"
                    onChange={(e) => {
                      setLigne(i, { prix: e.target.value });
                    }}
                  />
                </div>
                <div className="space-y-1">
                  <Label>TVA</Label>
                  <Select
                    value={l.tvaCode}
                    onChange={(e) => {
                      setLigne(i, { tvaCode: e.target.value });
                    }}
                  >
                    {TVA_OPTIONS.map((t) => (
                      <option key={t.code} value={t.code}>
                        {t.label}
                      </option>
                    ))}
                  </Select>
                </div>
                {l.kind === 'texte' ? (
                  <p className="self-center text-xs text-muted-foreground lg:col-span-2">
                    Le texte saisi par l'utilisateur deviendra le libellé de la ligne (prix 0 =
                    simple note, sans montant).
                  </p>
                ) : (
                  <div className="space-y-1 lg:col-span-2">
                    <Label>Libellé sur le devis (optionnel)</Label>
                    <Input
                      value={l.libelle}
                      placeholder="Par défaut : la question"
                      onChange={(e) => {
                        setLigne(i, { libelle: e.target.value });
                      }}
                    />
                  </div>
                )}
              </div>
              <div className="flex justify-end">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setLignes((ls) => ls.filter((_, j) => j !== i));
                  }}
                >
                  <Trash2 className="h-4 w-4" /> Retirer cette ligne
                </Button>
              </div>
            </div>
          ))}

          <Button
            variant="outline"
            onClick={() => {
              setLignes((ls) => [...ls, ligneVide()]);
            }}
          >
            <Plus className="h-4 w-4" /> Ajouter une ligne
          </Button>
        </CardContent>
      </Card>

      {saveMut.isError ? (
        <div className="rounded-md border border-destructive/30 bg-destructive/10 px-4 py-2 text-sm text-destructive">
          {messageErreur(saveMut.error)}
        </div>
      ) : null}

      <div className="flex items-center gap-3">
        <Button
          disabled={!canSave || saveMut.isPending}
          onClick={() => {
            saveMut.mutate();
          }}
        >
          {saveMut.isPending ? 'Enregistrement…' : isEdit ? 'Enregistrer' : 'Créer le devis guidé'}
        </Button>
        <Button asChild variant="outline">
          <Link to="/devis/qcm">Annuler</Link>
        </Button>
        {!canSave ? (
          <span className="text-sm text-muted-foreground">Un nom et au moins une ligne.</span>
        ) : null}
      </div>
    </div>
  );
}
